import { UserApi } from '@/api';
import { tokenManager } from './token-manager';

/**
 * 认证助手 - 统一处理所有认证相关逻辑和平台差异
 */
export class AuthHelper {
  private static refreshPromise: Promise<string> | null = null;
  private static silentLoginPromise: Promise<boolean> | null = null;

  /**
   * 清除 Token
   *
   * 用于流式请求 401 重试等场景，强制清除当前 token 使下次 getValidTokenWithLogin 触发重新登录。
   */
  static clearToken(): void {
    tokenManager.clear();
  }

  /**
   * 获取有效 token（带自动登录）
   *
   * 用于流式请求等不经过 HTTP 拦截器的场景。
   * - 微信小程序：尝试静默登录
   * - H5：跳转登录页
   */
  static async getValidTokenWithLogin(): Promise<string> {
    let token = await this.getValidToken();

    if (!token) {
      // 尝试平台登录（微信小程序：静默登录，H5：返回 false）
      try {
        const success = await this.platformLogin();
        if (success) {
          token = tokenManager.getCurrentToken();
        }
      }
      catch (error) {
        console.error('平台登录失败:', error);
      }

      if (!token) {
        // 登录失败，和 handle401 逻辑一致：
        // - 微信小程序：静默登录失败，跳转首页
        // - H5：跳转登录页
        this.gotoLogin();
        throw new Error('请重新登录');
      }
    }

    return token;
  }

  /**
   * 获取有效token - 自动处理刷新
   */
  static async getValidToken(): Promise<string | null> {
    const tokenData = tokenManager.getTokenData();

    // 无token - 需要重新登录
    if (!tokenData) {
      return null;
    }

    // refresh token过期 - 需要重新登录
    if (tokenManager.isRefreshTokenExpired()) {
      tokenManager.clear();
      return null;
    }

    // access token有效 - 直接返回
    if (!tokenManager.shouldRefresh()) {
      return tokenData.access_token;
    }

    // 需要刷新token - 防并发刷新
    if (!this.refreshPromise) {
      this.refreshPromise = this.performTokenRefresh();
    }

    try {
      const newToken = await this.refreshPromise;
      this.refreshPromise = null;
      return newToken;
    }
    catch (error) {
      this.refreshPromise = null;
      // 刷新失败，清除token，但不在这里尝试静默登录
      // 让401拦截器统一处理
      tokenManager.clear();
      throw error;
    }
  }

  /**
   * 执行token刷新
   */
  private static async performTokenRefresh(): Promise<string> {
    const tokenData = tokenManager.getTokenData();
    if (!tokenData) {
      throw new Error('NO_REFRESH_TOKEN');
    }

    const response = await UserApi.refreshToken({
      refresh_token: tokenData.refresh_token,
    });
    tokenManager.setTokens(response);
    return response.access_token;
  }

  /**
   * 平台登录 - 根据平台执行不同策略
   */
  static async platformLogin(): Promise<boolean> {
    // #ifdef MP-WEIXIN
    return await this.wechatSilentLogin();
    // #endif

    // #ifdef H5
    return false; // H5需要用户主动登录
    // #endif

    return false;
  }

  /**
   * 微信静默登录 - 防并发
   */
  static async wechatSilentLogin(): Promise<boolean> {
    // 防止并发静默登录
    if (this.silentLoginPromise) {
      return await this.silentLoginPromise;
    }

    this.silentLoginPromise = this.performSilentLogin();
    try {
      const result = await this.silentLoginPromise;
      // 延迟清除Promise，防止短时间内多次登录导致token覆盖
      setTimeout(() => {
        this.silentLoginPromise = null;
      }, 3000); // 3秒冷却期
      return result;
    }
    catch (error) {
      this.silentLoginPromise = null;
      throw error;
    }
  }

  /**
   * 执行静默登录
   * 注意：后端会在首次登录时自动创建账号（如果用户未注册）
   */
  private static async performSilentLogin(): Promise<boolean> {
    // #ifdef MP-WEIXIN
    const { code } = await uni.login({ provider: 'weixin' });
    const res = await UserApi.login({ code, silent: true });

    if (res) {
      tokenManager.setTokens(res);
      return true;
    }
    // #endif
    return false;
  }

  /**
   * 处理401错误 - 平台差异处理
   * 注意：只有在token完全失效时才会到这里
   * token刷新失败会清除token，然后请求重发会走到这里
   */
  static async handle401(originalRequest: any, http: any): Promise<any> {
    // #ifdef MP-WEIXIN
    // 微信小程序：尝试静默重登录
    try {
      const success = await this.wechatSilentLogin();
      if (success) {
        // 如果原请求是refresh接口，直接返回新token数据，不再重试
        if (originalRequest.url?.includes('/v1/user/refresh')) {
          const tokenData = tokenManager.getTokenData();
          return Promise.resolve({
            data: {
              code: 200,
              data: tokenData,
            },
          });
        }

        // 其他请求：更新token header后重试
        const newToken = tokenManager.getCurrentToken();
        if (newToken && originalRequest.header) {
          originalRequest.header.Authorization = `Bearer ${newToken}`;
        }

        return http.request(originalRequest); // 重试原请求
      }
    }
    catch (error: any) {
      console.error('静默登录失败:', error);
      // 错误会传播到首页，由首页的"加载失败"界面统一处理
    }

    // 静默登录失败，错误传播到调用方
    return Promise.reject(new Error('Silent login failed'));
    // #endif

    // #ifdef H5
    // H5：直接跳转登录页
    this.gotoLogin();
    return Promise.reject(new Error('Authentication failed'));
    // #endif
  }

  /**
   * 跳转登录页 - 平台差异
   * 注：微信小程序跳转首页，由首页触发静默登录（后端自动注册）
   */
  static gotoLogin(): void {
    tokenManager.clear();

    // #ifdef MP-WEIXIN
    uni.reLaunch({ url: '/pages/tab/home/index' });
    // #endif

    // #ifdef H5
    uni.reLaunch({ url: '/pages/common/login/index' });
    // #endif
  }

  /**
   * 处理用户被拉黑
   */
  static handleUserBlocked(): void {
    tokenManager.clear();
    uni.clearStorageSync();
    this.gotoLogin();
  }
}
